import type { FirebaseApp, FirebaseOptions } from 'firebase/app'
import type { RemoteConfig } from 'firebase/remote-config'

import * as snugLogger from 'app/services/snugLogger'

export const THANKS_REMOTE_CONFIG_UPDATED_EVENT = 'thanks-remote-config-updated'

const DEFAULT_MIN_FETCH_INTERVAL_MS = 12 * 60 * 60 * 1000
const DEFAULT_FETCH_TIMEOUT_MS = 1500
const DEFAULT_SAMPLE_RATE_SENTINEL = -1
const RENTER_PRIVACY_CENTRE_REMOTE_CONFIG_KEY = 'renter_privacy_centre_enabled'

export interface RemoteConfigSnapshot {
  isOn: boolean | null
  sampleRate: number | null
  source: string
}

export interface RenterPrivacyCentreRemoteConfigSnapshot {
  isOn: boolean
  source: string
}

const FIREBASE_CONFIG: FirebaseOptions = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
}

const isFiniteNumber = (value: unknown): value is number => {
  return Number.isFinite(value)
}

const toFiniteNumberOrNull = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null
  const num = Number(value)
  return isFiniteNumber(num) ? num : null
}

export const parseBooleanString = (value: unknown): boolean | null => {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase()
  if (normalized === 'true') return true
  if (normalized === 'false') return false
  return null
}

const getMinimumFetchIntervalMs = (): number => {
  const value = toFiniteNumberOrNull(
    process.env.REACT_APP_FIREBASE_REMOTE_CONFIG_MIN_FETCH_INTERVAL_MS,
  )
  if (value === null || value < 0) return DEFAULT_MIN_FETCH_INTERVAL_MS
  return value
}

const getFetchTimeoutMs = (): number => {
  const value = toFiniteNumberOrNull(
    process.env.REACT_APP_FIREBASE_REMOTE_CONFIG_FETCH_TIMEOUT_MS,
  )
  if (value === null || value <= 0) return DEFAULT_FETCH_TIMEOUT_MS
  return value
}

const hasFirebaseConfig = (): boolean => {
  return Boolean(
    FIREBASE_CONFIG.apiKey &&
      FIREBASE_CONFIG.projectId &&
      FIREBASE_CONFIG.appId,
  )
}

const isRemoteConfigEnabled = (): boolean => {
  return process.env.REACT_APP_THANKS_REMOTE_CONFIG_ENABLED !== 'false'
}

const emitRemoteConfigUpdated = (): void => {
  if (typeof window === 'undefined') return

  if (typeof window.Event === 'function') {
    window.dispatchEvent(new window.Event(THANKS_REMOTE_CONFIG_UPDATED_EVENT))
    return
  }

  if (document?.createEvent) {
    const evt = document.createEvent('Event')
    evt.initEvent(THANKS_REMOTE_CONFIG_UPDATED_EVENT, false, false)
    window.dispatchEvent(evt)
  }
}

const DEFAULT_SNAPSHOT: RemoteConfigSnapshot = {
  isOn: null,
  sampleRate: null,
  source: 'uninitialized',
}

const DEFAULT_RENTER_PRIVACY_CENTRE_SNAPSHOT: RenterPrivacyCentreRemoteConfigSnapshot =
  {
    isOn: false,
    source: 'uninitialized',
  }

let snapshot: RemoteConfigSnapshot = { ...DEFAULT_SNAPSHOT }
let renterPrivacyCentreSnapshot: RenterPrivacyCentreRemoteConfigSnapshot = {
  ...DEFAULT_RENTER_PRIVACY_CENTRE_SNAPSHOT,
}
let initializePromise: Promise<void> | null = null
interface FirebaseModules {
  initializeApp: (config: FirebaseOptions) => FirebaseApp
  getApps: () => FirebaseApp[]
  isSupported: () => Promise<boolean>
  getRemoteConfig: (app?: FirebaseApp) => RemoteConfig
  getValue: (
    rc: RemoteConfig,
    key: string,
  ) => { asString: () => string; asNumber: () => number }
  fetchAndActivate: (rc: RemoteConfig) => Promise<boolean>
}

let firebaseModulesPromise: Promise<FirebaseModules> | null = null

const updateSnapshotFromRemoteConfig = (
  getValue: FirebaseModules['getValue'],
  remoteConfig: RemoteConfig,
  source: string,
): void => {
  const enabledValue = parseBooleanString(
    getValue(remoteConfig, 'thanks_offers_enabled').asString(),
  )
  const sampleRateValue = Number(
    getValue(remoteConfig, 'thanks_offers_sample_rate').asNumber(),
  )

  snapshot = {
    isOn: enabledValue,
    sampleRate:
      isFiniteNumber(sampleRateValue) &&
      sampleRateValue !== DEFAULT_SAMPLE_RATE_SENTINEL
        ? sampleRateValue
        : null,
    source,
  }
}

const updateRenterPrivacyCentreSnapshotFromRemoteConfig = (
  getValue: FirebaseModules['getValue'],
  remoteConfig: RemoteConfig,
  source: string,
): void => {
  const enabledValue = parseBooleanString(
    getValue(remoteConfig, RENTER_PRIVACY_CENTRE_REMOTE_CONFIG_KEY).asString(),
  )

  renterPrivacyCentreSnapshot = {
    isOn: enabledValue === true,
    source,
  }
}

const loadFirebaseModules = (): Promise<FirebaseModules> => {
  if (firebaseModulesPromise) return firebaseModulesPromise

  firebaseModulesPromise = Promise.all([
    import('firebase/app'),
    import('firebase/remote-config'),
  ]).then(([appModule, remoteConfigModule]) => ({
    initializeApp: appModule.initializeApp,
    getApps: appModule.getApps,
    isSupported: remoteConfigModule.isSupported,
    getRemoteConfig: remoteConfigModule.getRemoteConfig,
    getValue: remoteConfigModule.getValue,
    fetchAndActivate: remoteConfigModule.fetchAndActivate,
  }))

  return firebaseModulesPromise
}

export const initializeThanksRemoteConfig = (): Promise<void> => {
  if (initializePromise) return initializePromise

  if (typeof window === 'undefined') {
    initializePromise = Promise.resolve()
    return initializePromise
  }

  if (!isRemoteConfigEnabled() || !hasFirebaseConfig()) {
    snapshot = { ...DEFAULT_SNAPSHOT, source: 'disabled-or-missing-config' }
    renterPrivacyCentreSnapshot = {
      ...DEFAULT_RENTER_PRIVACY_CENTRE_SNAPSHOT,
      source: 'disabled-or-missing-config',
    }
    initializePromise = Promise.resolve()
    return initializePromise
  }

  initializePromise = loadFirebaseModules()
    .then((firebase) => {
      return firebase.isSupported().then((supported): Promise<void> | void => {
        if (!supported) {
          snapshot = { ...DEFAULT_SNAPSHOT, source: 'not-supported' }
          renterPrivacyCentreSnapshot = {
            ...DEFAULT_RENTER_PRIVACY_CENTRE_SNAPSHOT,
            source: 'not-supported',
          }
          return
        }

        const existingApps = firebase.getApps()
        const app =
          existingApps.length > 0
            ? existingApps[0]
            : firebase.initializeApp(FIREBASE_CONFIG)
        const remoteConfig = firebase.getRemoteConfig(app)

        remoteConfig.settings = {
          minimumFetchIntervalMillis: getMinimumFetchIntervalMs(),
          fetchTimeoutMillis: getFetchTimeoutMs(),
        }
        remoteConfig.defaultConfig = {
          thanks_offers_enabled: false,
          thanks_offers_sample_rate: DEFAULT_SAMPLE_RATE_SENTINEL,
        }

        // Read any cached value immediately, then refresh from network.
        updateSnapshotFromRemoteConfig(
          firebase.getValue,
          remoteConfig,
          'cached-or-default',
        )
        updateRenterPrivacyCentreSnapshotFromRemoteConfig(
          firebase.getValue,
          remoteConfig,
          'cached-or-default',
        )
        emitRemoteConfigUpdated()

        return firebase
          .fetchAndActivate(remoteConfig)
          .then(() => {
            updateSnapshotFromRemoteConfig(
              firebase.getValue,
              remoteConfig,
              'remote',
            )
            updateRenterPrivacyCentreSnapshotFromRemoteConfig(
              firebase.getValue,
              remoteConfig,
              'remote',
            )
            emitRemoteConfigUpdated()
          })
          .catch(() => {
            updateSnapshotFromRemoteConfig(
              firebase.getValue,
              remoteConfig,
              'cached-or-default',
            )
            updateRenterPrivacyCentreSnapshotFromRemoteConfig(
              firebase.getValue,
              remoteConfig,
              'cached-or-default',
            )
            emitRemoteConfigUpdated()
          })
      })
    })
    .catch((error: unknown) => {
      snugLogger.warn('Firebase Remote Config initialization failed', error)
      snapshot = { ...DEFAULT_SNAPSHOT, source: 'init-error' }
      renterPrivacyCentreSnapshot = {
        ...DEFAULT_RENTER_PRIVACY_CENTRE_SNAPSHOT,
        source: 'init-error',
      }
    })

  return initializePromise
}

export const getThanksOffersRemoteConfigSnapshot = (): RemoteConfigSnapshot => {
  return snapshot
}

export const getRenterPrivacyCentreRemoteConfigSnapshot =
  (): RenterPrivacyCentreRemoteConfigSnapshot => {
    return renterPrivacyCentreSnapshot
  }
